/**
 * Wraps email HTML body content in a complete HTML document structure
 * with proper meta tags, responsive styles, and email client compatibility
 * 
 * @param {string} bodyHtml - The HTML body content (e.g., from RichTextEditor)
 * @param {boolean} forPreview - If true, uses minimal padding for preview display
 * @returns {string} - Complete HTML document ready for email sending
 */
export const wrapEmailHtml = (bodyHtml, forPreview = false) => {
  const emailStyles = `
    /* Reset styles for email clients */
    body, table, td, a { 
      -webkit-text-size-adjust: 100%; 
      -ms-text-size-adjust: 100%; 
    }
    table, td { 
      mso-table-lspace: 0pt; 
      mso-table-rspace: 0pt; 
    }
    img { 
      -ms-interpolation-mode: bicubic; 
      border: 0; 
      height: auto; 
      line-height: 100%; 
      outline: none; 
      text-decoration: none; 
    }
    
    /* Base styles */
    body {
      margin: 0 !important;
      padding: 0 !important;
      width: 100% !important;
      height: 100% !important;
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
      font-size: 16px;
      line-height: 1.6;
      color: #333333;
      background-color: ${forPreview ? '#ffffff' : '#f4f4f4'};
      word-break: break-all;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    
    /* Content container */
    .email-container {
      max-width: ${forPreview ? '100%' : '600px'};
      margin: 0 auto;
      background-color: #ffffff;
      padding: ${forPreview ? '1.5rem' : '20px'};
    }
    
    /* Typography */
    h1, h2, h3, h4, h5, h6 {
      margin-top: 0;
      margin-bottom: 1rem;
      font-weight: 600;
      line-height: 1.3;
      color: #1a1a1a;
    }
    
    h1 { font-size: 30px; } /* 1.875rem */
    h2 { font-size: 24px; } /* 1.5rem */
    h3 { font-size: 20px; } /* 1.25rem */
    h4 { font-size: 18px; } /* 1.125rem */
    h5 { font-size: 16px; } /* 1rem */
    h6 { font-size: 14px; } /* 0.875rem */
    
    p {
      margin: 0 0 16px 0;
      line-height: 1.6;
    }
    
    a {
      color: #2563eb;
      text-decoration: underline;
      word-break: break-word;
    }
    
    a:hover {
      color: #1d4ed8;
    }
    
    /* Lists */
    ul, ol {
      margin: 0 0 16px 0;
      padding-left: 24px;
    }
    
    li {
      margin-bottom: 8px;
      line-height: 1.6;
    }
    
    /* Tables */
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 16px 0;
    }
    
    table td, table th {
      border: 1px solid #e5e7eb;
      padding: 12px;
      text-align: left;
    }
    
    table th {
      background-color: #f9fafb;
      font-weight: 600;
      color: #1f2937;
    }
    
    /* Images */
    img {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 16px 0;
    }
    
    /* Blockquote */
    blockquote {
      margin: 16px 0;
      padding: 12px 16px;
      border-left: 4px solid #e5e7eb;
      background-color: #f9fafb;
      font-style: italic;
      color: #6b7280;
    }
    
    /* Code */
    code {
      background-color: #f3f4f6;
      padding: 2px 6px;
      border-radius: 4px;
      font-family: "Courier New", Courier, monospace;
      font-size: 14px;
      color: #1f2937;
    }
    
    pre {
      background-color: #f3f4f6;
      padding: 16px;
      border-radius: 6px;
      overflow-x: auto;
      margin: 16px 0;
    }
    
    pre code {
      background-color: transparent;
      padding: 0;
    }
    
    /* Horizontal rule */
    hr {
      border: none;
      border-top: 2px solid #e5e7eb;
      margin: 24px 0;
    }
    
    /* Strong and emphasis */
    strong, b {
      font-weight: 700;
      color: #1a1a1a;
    }
    
    em, i {
      font-style: italic;
    }
    
    /* Text alignment classes */
    .text-left { text-align: left; }
    .text-center { text-align: center; }
    .text-right { text-align: right; }
    
    /* Responsive styles */
    @media only screen and (max-width: 600px) {
      .email-container {
        width: 100% !important;
        padding: 16px !important;
      }
      
      h1 { font-size: 24px !important; }
      h2 { font-size: 20px !important; }
      h3 { font-size: 18px !important; }
      
      table {
        font-size: 14px !important;
      }
      
      table td, table th {
        padding: 8px !important;
      }
    }
    
    /* Dark mode support (for email clients that support it) */
    @media (prefers-color-scheme: dark) {
      body {
        background-color: #1f2937 !important;
      }
      
      .email-container {
        background-color: #111827 !important;
      }

      body, .email-container, body * {
        color: #ffffff !important;
      }
      
      a, a * {
        color: #60a5fa !important;
      }
      
      blockquote {
        background-color: #1f2937 !important;
        border-left-color: #4b5563 !important;
      }
      
      code, pre {
        background-color: #1f2937 !important;
      }
      
      table td, table th {
        border-color: #374151 !important;
      }
      
      table th {
        background-color: #1f2937 !important;
      }
      
      hr {
        border-top-color: #374151 !important;
      }
    }
  `;

  const completeHtmlDocument = `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="x-apple-disable-message-reformatting">
  <meta name="format-detection" content="telephone=no,address=no,email=no,date=no,url=no">
  <title>Email</title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
  <style type="text/css">
${emailStyles}
  </style>
</head>
<body>
  <div class="email-container">
${bodyHtml || ''}
  </div>
</body>
</html>`;

  return completeHtmlDocument;
};

/**
 * Check if HTML content is already a complete document
 * @param {string} html - HTML content to check
 * @returns {boolean} - True if it's already a complete document
 */
export const isCompleteHtmlDocument = (html) => {
  if (!html || typeof html !== 'string') return false;
  
  const trimmedHtml = html.trim().toLowerCase();
  return trimmedHtml.startsWith('<!doctype') || 
         trimmedHtml.startsWith('<html');
};

/**
 * Smart wrapper that only wraps if needed
 * @param {string} html - HTML content
 * @param {boolean} forPreview - If true, uses minimal padding for preview display
 * @returns {string} - Complete HTML document
 */
export const ensureCompleteEmailHtml = (html, forPreview = false) => {
  if (isCompleteHtmlDocument(html)) {
    return html;
  }
  return wrapEmailHtml(html, forPreview);
};

/**
 * Dynamically injects word-wrapping and overflow-prevention CSS styles into an HTML document.
 * @param {string} html - Raw HTML document string
 * @returns {string} - Modified HTML string with responsive CSS injected
 */
export const injectResponsiveStyles = (html) => {
  if (!html || typeof html !== "string") return html;

  const styleBlock = `
<style type="text/css">
  body, table, td, p, div, span, li, a {
    word-break: break-all !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
  }
</style>
`;

  if (html.includes("</head>")) {
    return html.replace("</head>", `${styleBlock}</head>`);
  } else if (html.includes("</HEAD>")) {
    return html.replace("</HEAD>", `${styleBlock}</HEAD>`);
  }

  return styleBlock + html;
};

